/* =============================================================================
   VIEWS / ADMIN / ARCHIVE — Historisches Archiv (Top-Level + Section-Dispatch)
   =============================================================================
   S182 Cut 6: Archiv auf zwei Sektionen reduziert (Bestellungen + E-Mails).
   S213-A-HF: Bons-Sektion wieder aufgenommen (Jonas — Personal kontrolliert /
   druckt Bons öfter nach; damit kommt der „📄 PDF"-Zugriff zurück).
   S237 T1: Z-Bons-Sektion wieder angeschlossen — der Tagesabschluss-Tab zeigt
   NUR den Stichtags-Beleg, die GoBD-Historie (§146 AO) gehört ins Archiv; das
   Z-Bon-Beleg-Modal verweist seit jeher auf „Auswertung → Archiv → Z-Bons".
   Reservierungs-Details bleiben über die zugehörige Bestellung erreichbar
   (Memory feedback_gg_archive_single_source); archive-reservations.js bleibt
   ungenutzt.

   Diese Datei: Public API, Top-Level-Renderer, Section-Dispatch, Re-Exports
   (Backward-Compat für archive.test.js) + window.*-Bindings.

   Sub-Sektionen:
     - archive-state.js   _state + getState + defaultDateRange
     - archive-orders.js  Orders-Sub-Section + loadOrders
     - archive-mails.js   Mails-Liste (unified) + Realtime + Popup + CRUD
     - archive-bons.js    Bons-Sub-Section + loadBons + „📄 PDF" (S213-A-HF)
     - archive-zbons.js   Z-Bons-Sub-Section + loadZBons + „📄 PDF"/„✉" (S237)
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { registerLiveView } from '../../core/live-view-registry.js';
import { _state } from './archive-state.js';
import {
  loadOrders,
  getFilteredOrders,
  renderOrdersArchive,
  renderOrderStatusBadge,
  exportArchiveOrdersCSV
} from './archive-orders.js';
import {
  loadBons,
  renderBonsFilters,
  renderBonsArchive,
  getFilteredBons,
  exportArchiveBonsCSV,
  backfillBonArchive
} from './archive-bons.js';
import {
  loadZBons,
  renderZBonsFilters,
  renderZBonsArchive,
  getFilteredZBons,
  zBonStatusBadges,
  exportArchiveZBonsCSV
} from './archive-zbons.js';
import {
  loadMails,
  _wireMailsSubscription,
  _unwireMailsSubscription,
  _shouldRefreshOnMailEvent,
  renderMailsArchive,
  renderDeletedMailsList,
  deleteMail,
  restoreMail,
  openMailPopup,
  closeMailPopup,
  openMailAttachment,
  mailHasAttachments,
  attachmentToDataUrl,
  attachmentOpenable,
  buildMailPreview,
  _buildMailRowHtml,
  _formatMailFailureError,
  _formatMailKind
} from './archive-mails.js';

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export function setArchiveSection(section) {
  if (!['orders', 'bons', 'zbons', 'mails'].includes(section)) return;
  const prev = _state.section;
  _state.section = section;
  if (section === 'orders' && _state.ordersCache === null && !_state.ordersLoading) {
    loadOrders();
  }
  // S213-A-HF: Bon-Archiv-Cache beim Tab-Öffnen (lazy) auffrischen — nicht am
  // Boot der Archiv-View. loadBons rendert nach dem Refresh non-destruktiv nach.
  if (section === 'bons' && !_state.bonsLoading) {
    loadBons();
  }
  // S237 T1: gleiches Lazy-Muster für die Z-Bons (Multi-Device-Nachzug).
  if (section === 'zbons' && !_state.zbonsLoading) {
    loadZBons();
  }
  if (prev === 'mails' && section !== 'mails') {
    _unwireMailsSubscription();
  }
  if (section === 'mails') {
    loadMails();
    _wireMailsSubscription();
  }
  renderArchive();
}

export function setArchiveFilter(key, value) {
  if (!(key in _state)) return;
  _state[key] = value;
  if (key === 'dateFrom' || key === 'dateTo') {
    _state.ordersCache = null;
    if (_state.section === 'orders') loadOrders();
  }
  // Memory feedback_gg_non_destructive_render: Suche/Status feuern bei jedem
  // Tastendruck → nur Ergebnis-DIV neu rendern (Focus/Buffer bleiben). Bei
  // Datum/Section bleibt voller Re-Render (Filter-Form selbst muss refreshen).
  if (key === 'search' || key === 'status') {
    renderResultsOnly();
    return;
  }
  renderArchive();
}

/** Non-destruktiver Refresh: ersetzt nur den Inhalt von .archive-results. */
export function renderResultsOnly() {
  const tab = document.getElementById('tab-archive');
  if (!tab) return;
  const resultsWrap = tab.querySelector('.archive-results');
  if (!resultsWrap) { renderArchive(); return; }
  resultsWrap.innerHTML = _renderArchiveResults();
}

// -----------------------------------------------------------------------------
// Live-Registry (S237 T1 — die Registrierung aus S30 war beim Cut-6-Umbau
// verloren gegangen; realtime.js emittiert `bonarchive:realtime` +
// `zbonarchive:realtime` seit S30 unverändert).
//
// Render-Fn ist bewusst `renderResultsOnly` (non-destruktiv, nur
// `.archive-results`), damit Filter-Inputs Fokus + Tipp-Puffer behalten
// (Memory feedback_gg_non_destructive_render). Mails haben ihr EIGENES
// Subscription-Handling (`_wireMailsSubscription`) und sind hier NICHT
// verdrahtet — sonst doppelter Refresh pro Mail-Event.
// -----------------------------------------------------------------------------

let _archiveLiveRegistered = false;
function ensureArchiveLiveRegistered() {
  if (_archiveLiveRegistered) return;
  _archiveLiveRegistered = true;
  registerLiveView({
    tabId: 'tab-archive',
    render: renderResultsOnly,
    events: ['bonarchive:realtime', 'zbonarchive:realtime']
  });
}

// -----------------------------------------------------------------------------
// Top-Level-Renderer
// -----------------------------------------------------------------------------

export function renderArchive() {
  const tab = document.getElementById('tab-archive');
  if (!tab) return;
  ensureArchiveLiveRegistered();

  tab.innerHTML = `
    <div class="dash-card-head" style="margin-bottom: var(--space-4);">
      <h2 style="margin:0;">Archiv</h2>
      <span style="font-size:.85rem; color: var(--ink-muted);">
        Bestellungen, Bons, Z-Bons &amp; E-Mails der Vergangenheit — Daten in der DB, Cache-unabhängig.
      </span>
    </div>

    <div class="archive-section-tabs">
      <button class="${_state.section === 'orders' ? 'active' : ''}"
              onclick="setArchiveSection('orders')">
        🛒 Bestellungen
      </button>
      <button class="${_state.section === 'bons' ? 'active' : ''}"
              onclick="setArchiveSection('bons')">
        🧾 Bons
      </button>
      <button class="${_state.section === 'zbons' ? 'active' : ''}"
              onclick="setArchiveSection('zbons')">
        📊 Z-Bons
      </button>
      <button class="${_state.section === 'mails' ? 'active' : ''}"
              onclick="setArchiveSection('mails')">
        📧 E-Mails
      </button>
    </div>

    ${_renderArchiveTopArea()}

    <div class="archive-results">
      ${_renderArchiveResults()}
    </div>
  `;
}

/** Section-Dispatch für die Ergebnis-Liste (orders | bons | zbons | mails). */
function _renderArchiveResults() {
  if (_state.section === 'mails') return renderMailsArchive();
  if (_state.section === 'bons')  return renderBonsArchive();
  if (_state.section === 'zbons') return renderZBonsArchive();
  return renderOrdersArchive();
}

/**
 * Filter-Leiste je Section. orders: Datum/Status/Suche. mails: KEINE Filter-
 * Leiste — die Mail-Liste zeigt bewusst IMMER alle (nicht-gelöschten) Mails;
 * Status ist nur Zähl-Kopfzeile + Spalte (Scope-Korrektur ggü. Cut-6-Spec).
 */
function _renderArchiveTopArea() {
  if (_state.section === 'mails') return '';
  // Bons + Z-Bons haben eigene Filter-Leisten (Datum + Suche, kein Status-Select).
  if (_state.section === 'bons')  return renderBonsFilters();
  if (_state.section === 'zbons') return renderZBonsFilters();
  return renderArchiveFilters();
}

function renderArchiveFilters() {
  const statusOptions = [
    { v: 'all',       l: 'Alle' },
    { v: 'picked_up', l: 'Abgeholt' },
    { v: 'cancelled', l: 'Storniert' },
    { v: 'no_show',   l: 'No-Show' }
  ];
  return `
    <div class="archive-filters">
      <div class="archive-filter-row">
        <label>
          <span>Zeitraum von</span>
          <input type="date" value="${_state.dateFrom}"
                 onchange="setArchiveFilter('dateFrom', this.value)">
        </label>
        <label>
          <span>bis</span>
          <input type="date" value="${_state.dateTo}"
                 onchange="setArchiveFilter('dateTo', this.value)">
        </label>
        <label>
          <span>Status</span>
          <select onchange="setArchiveFilter('status', this.value)">
            ${statusOptions.map(o => `
              <option value="${o.v}" ${_state.status === o.v ? 'selected' : ''}>${o.l}</option>
            `).join('')}
          </select>
        </label>
        <label class="archive-filter-search">
          <span>Suche</span>
          <input type="search" placeholder="Name, E-Mail, Telefon, Bestell-Nr…"
                 value="${htmlEscape(_state.search)}"
                 oninput="setArchiveFilter('search', this.value)">
        </label>
      </div>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Re-Exports — Backward-Compat für archive.test.js + Drittnutzung
// -----------------------------------------------------------------------------
export {
  exportArchiveOrdersCSV,
  getFilteredOrders,
  renderOrderStatusBadge,
  // Bons Public (S213-A-HF)
  loadBons,
  getFilteredBons,
  exportArchiveBonsCSV,
  // Z-Bons Public (S237 T1)
  loadZBons,
  getFilteredZBons,
  zBonStatusBadges,
  exportArchiveZBonsCSV,
  // Mails Public + Test-Helpers
  renderDeletedMailsList,
  deleteMail,
  restoreMail,
  openMailPopup,
  closeMailPopup,
  openMailAttachment,
  mailHasAttachments,
  attachmentToDataUrl,
  attachmentOpenable,
  buildMailPreview,
  _shouldRefreshOnMailEvent,
  _buildMailRowHtml,
  _formatMailFailureError,
  _formatMailKind
};

// -----------------------------------------------------------------------------
// Side-Effect: window.* für Inline-Handler (onclick="…")
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderArchive,
    renderResultsOnly,
    setArchiveSection,
    setArchiveFilter,
    exportArchiveOrdersCSV,
    // S213-A-HF — CSV-Export-Button der Bons-Sektion (onclick, CSP über dom-bindings).
    // openArchivedBonPdf ist bereits aus orders-receipt-actions.js global gebridged.
    exportArchiveBonsCSV,
    // S248 — Nachtrag fehlender Teilbeleg-Bons (einmalige Owner-Aktion).
    backfillBonArchive,
    // S237 T1 — CSV-Export-Button der Z-Bons-Sektion (onclick, CSP über
    // dom-bindings). zBonActionPdf / resendArchivedZBonEmail / viewArchivedZBon
    // sind bereits aus day-close.js global gebridged.
    exportArchiveZBonsCSV,
    renderDeletedMailsList,
    deleteMail,
    restoreMail,
    openMailPopup,
    closeMailPopup,
    openMailAttachment
  });
}
